import React, { useMemo, useState } from "react"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"
import { useQuery } from "@apollo/client"
import SEARCH_CAMPAIGNS from "../../../graphql/queries/campaign/SearchCampaigns"
import SEARCH_CAMPAIGN_GROUPS from "../../../graphql/queries/campaign/SearchCampaignGroups"
import { handleErrorMessages } from "helpers/errors"
import { CampaignSearchHit } from "../../../lib/types/generated/graphql-types"
import {
  ButtonContainer,
  ButtonWrapper,
  CampaignDates,
  CampaignName,
  ChartContent,
  ChartContentInner,
  ChartHeader,
  ChartRow,
  ChartWrapper,
  ColorInfo,
  Container,
  ExpandIcon,
  GroupHeader,
  Header,
  HeaderCell,
  LongRangeCampaign,
  LongRangeContent,
  LongRangeContentInner,
  LongRangeSection,
  LongRangeWrapper,
  MonthButton,
  MonthCell,
  SkeletonBar,
  SkeletonGroup,
  SkeletonGroupHeader,
  TimelineTrack
} from "./CampaignTimeLine.styled"
import {
  StyledTooltip,
  TooltipAction,
  TooltipContent,
  TooltipRow,
  TooltipTitle
} from "./CampaignBar.styled"
import { ReactComponent as ChevronDown } from "images/icons/chevron-down.svg"
import { ReactComponent as ChartIcon } from "images/icons/chart-simple-horizontal.svg"
import { ReactComponent as CalendarClockIcon } from "images/icons/calendar-clock.svg"
import { ReactComponent as ArrowRightFromLine } from "images/icons/arrow-right-from-line.svg"
import { ReactComponent as ArrowRightToLine } from "images/icons/arrow-right-to-line.svg"
import { theme } from "../../../theme"
import CampaignBar, { CampaignData } from "./CampaignBar"
import { useNavigate } from "react-router-dom"

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

interface TransformedCampaign {
  name: string
  id: string
  startDate: string
  endDate: string
  color: string
  campaignGroupId: string
}

interface CampaignTimeLineProps {
  previewCampaign?: {
    name: string
    startDate: string
    endDate: string
  }
}

const CampaignTimeLine = ({ previewCampaign }: CampaignTimeLineProps = {}) => {
  const [campaigns, setCampaigns] = useState<CampaignSearchHit[]>([])
  const [campaignGroups, setCampaignGroups] = useState<Record<string, string>>({})
  const [isLongRangeExpanded, setIsLongRangeExpanded] = useState(false)
  const [isChartExpanded, setIsChartExpanded] = useState(true)
  const [monthsToShow, setMonthsToShow] = useState(3)
  const [hoveredCampaign, setHoveredCampaign] = useState<CampaignData | null>(null)

  const navigate = useNavigate()

  const generateMonths = () => {
    const startDate = dayjs().subtract(1, "month").startOf("month")
    return Array.from({ length: monthsToShow }, (_, index) => {
      const monthStart = startDate.add(index, "month").startOf("month")
      const monthEnd = monthStart.endOf("month")
      return {
        name: monthStart.format(monthsToShow > 6 ? "MMM YY" : "MMM YYYY"),
        start: monthStart.toISOString(),
        end: monthEnd.toISOString()
      }
    })
  }

  const queryVariables = useMemo(() => {
    const now = dayjs().toISOString()
    return {
      from: 0,
      size: 100,
      sort: [{ field: "startDate", order: "ASC" }],
      query: JSON.stringify({
        range: {
          endDate: {
            gte: now
          }
        }
      })
    }
  }, [])

  const { loading: campaignsLoading } = useQuery(SEARCH_CAMPAIGNS, {
    variables: queryVariables,
    onCompleted: (data) => {
      setCampaigns(data?.searchCampaigns?.hits || [])
    },
    onError: (err) => {
      handleErrorMessages(err)
    }
  })

  useQuery(SEARCH_CAMPAIGN_GROUPS, {
    variables: {
      from: 0,
      size: 1000,
      sort: [{ field: "updated", order: "DESC" }]
    },
    onCompleted: (data) => {
      const groups: Record<string, string> = {}
      if (data?.searchCampaignGroups?.hits) {
        data.searchCampaignGroups.hits.forEach((group: { id: string; name: string }) => {
          groups[group.id] = group.name
        })
      }
      setCampaignGroups(groups)
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const months = generateMonths()

  const filterAndTransformCampaigns = () => {
    const now = dayjs()
    const shortTermCampaigns: TransformedCampaign[] = []
    const longRangeCampaigns: CampaignSearchHit[] = []
    campaigns.forEach((campaign: CampaignSearchHit) => {
      const start = dayjs(campaign.startDate)
      const end = dayjs(campaign.endDate)
      const yearsDiff = end.diff(start, "year", true)

      if (yearsDiff > 1) {
        longRangeCampaigns.push(campaign)
      } else {
        let color = theme.colors.yellow

        if (now.isBetween(start, end, null, "[]")) {
          color = theme.colors.turquoiseDark
        }

        shortTermCampaigns.push({
          id: campaign.id,
          name: campaign.name,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          color,
          campaignGroupId: campaign.campaignGroupId || ""
        })
      }
    })
    return { shortTermCampaigns, longRangeCampaigns }
  }

  const { shortTermCampaigns, longRangeCampaigns } = filterAndTransformCampaigns()

  const campaignsWithPreview = previewCampaign
    ? [
        ...shortTermCampaigns,
        {
          id: "preview",
          name: previewCampaign.name,
          startDate: previewCampaign.startDate,
          endDate: previewCampaign.endDate,
          color: theme.colors.blue,
          campaignGroupId: "preview"
        }
      ]
    : shortTermCampaigns

  const groupedCampaignsObj = campaignsWithPreview.reduce(
    (acc, campaign) => {
      const groupId = campaign.campaignGroupId || "ungrouped"
      if (!acc[groupId]) {
        acc[groupId] = []
      }
      acc[groupId].push(campaign)
      return acc
    },
    {} as Record<string, TransformedCampaign[]>
  )

  const groupedCampaigns = Object.entries(groupedCampaignsObj)
    .sort(([keyA], [keyB]) => {
      if (keyA === "preview") return -1
      if (keyB === "preview") return 1
      return 0
    })
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value
        return acc
      },
      {} as Record<string, TransformedCampaign[]>
    )

  const getBarPositionInTimeline = (campaign: TransformedCampaign) => {
    const timelineStart = dayjs(months[0].start)
    const timelineEnd = dayjs(months[months.length - 1].end)
    const campaignStart = dayjs(campaign.startDate)
    const campaignEnd = dayjs(campaign.endDate)

    const timelineDuration = timelineEnd.diff(timelineStart)

    const campaignStartOffset = campaignStart.diff(timelineStart)
    const campaignDuration = campaignEnd.diff(campaignStart)

    const startPercent = (campaignStartOffset / timelineDuration) * 100
    const widthPercent = (campaignDuration / timelineDuration) * 100

    return {
      barStart: `${Math.max(0, startPercent)}%`,
      barWidth: `${widthPercent}%`
    }
  }

  return (
    <>
      <StyledTooltip
        id="campaign-timeline-tooltip"
        delayShow={0}
        delayHide={0}
        render={() =>
          hoveredCampaign ? (
            <TooltipContent>
              <TooltipTitle>{hoveredCampaign.campaignName}</TooltipTitle>
              <TooltipRow>
                <ArrowRightFromLine />
                {dayjs(hoveredCampaign.startDate).format("YYYY-MM-DD, HH:mm")}
              </TooltipRow>
              <TooltipRow>
                <ArrowRightToLine />
                {dayjs(hoveredCampaign.endDate).format("YYYY-MM-DD, HH:mm")}
              </TooltipRow>
              {!hoveredCampaign.isPreview && <TooltipAction>Click to edit</TooltipAction>}
            </TooltipContent>
          ) : null
        }
      />
      <Container>
        <ChartHeader onClick={() => setIsChartExpanded(!isChartExpanded)}>
          <span>
            {" "}
            <ChartIcon />
            Campaign timeline
          </span>
          <ExpandIcon expanded={isChartExpanded}>
            <ChevronDown />
          </ExpandIcon>
        </ChartHeader>
        <ChartContent expanded={isChartExpanded}>
          <ChartContentInner expanded={isChartExpanded}>
            <ButtonContainer>
              <div>
                <ColorInfo $color={theme.colors.turquoiseDark}>Active campaigns</ColorInfo>
                <ColorInfo $color={theme.colors.yellow}>Upcoming campaigns</ColorInfo>
                {previewCampaign && <ColorInfo $color={theme.colors.blue}>New Campaign</ColorInfo>}
              </div>
              <ButtonWrapper>
                <MonthButton active={monthsToShow === 3} onClick={() => setMonthsToShow(3)}>
                  3 Months
                </MonthButton>
                <MonthButton active={monthsToShow === 6} onClick={() => setMonthsToShow(6)}>
                  6 Months
                </MonthButton>
                <MonthButton active={monthsToShow === 12} onClick={() => setMonthsToShow(12)}>
                  12 Months
                </MonthButton>
              </ButtonWrapper>
            </ButtonContainer>
            <ChartWrapper>
              <Header monthCount={monthsToShow}>
                {months.map((month) => (
                  <HeaderCell key={month.name}>{month.name}</HeaderCell>
                ))}
              </Header>

              {campaignsLoading ? (
                <>
                  {[1, 2, 3].map((groupIndex) => (
                    <SkeletonGroup key={groupIndex}>
                      <SkeletonGroupHeader />
                      {[1, 2, 3].map((barIndex) => (
                        <SkeletonBar key={barIndex} width={`${40 + barIndex * 15}%`} />
                      ))}
                    </SkeletonGroup>
                  ))}
                </>
              ) : (
                Object.entries(groupedCampaigns).map(([groupId, campaigns]) => (
                  <div key={groupId}>
                    <GroupHeader>
                      {groupId === "preview"
                        ? "New Campaign (Preview)"
                        : groupId === "ungrouped"
                          ? "Ungrouped Campaigns"
                          : campaignGroups[groupId] || groupId}
                    </GroupHeader>
                    {campaigns.map((campaign, index) => {
                      const { barStart, barWidth } = getBarPositionInTimeline(campaign)

                      return (
                        <ChartRow key={index}>
                          <TimelineTrack monthCount={monthsToShow}>
                            {months.map((_, monthIndex) => (
                              <MonthCell key={monthIndex} />
                            ))}
                          </TimelineTrack>
                          <CampaignBar
                            color={campaign.color}
                            width={barWidth}
                            start={barStart}
                            campaignId={campaign.id}
                            campaignName={campaign.name}
                            startDate={campaign.startDate}
                            endDate={campaign.endDate}
                            showName={true}
                            isPreview={campaign.id === "preview"}
                            onHoverStart={setHoveredCampaign}
                            onHoverEnd={() => setHoveredCampaign(null)}
                          />
                        </ChartRow>
                      )
                    })}
                  </div>
                ))
              )}
            </ChartWrapper>
          </ChartContentInner>
        </ChartContent>
      </Container>

      {longRangeCampaigns.length > 0 && (
        <LongRangeSection>
          <ChartHeader onClick={() => setIsLongRangeExpanded(!isLongRangeExpanded)}>
            <span>
              <CalendarClockIcon />
              Active long-term campaigns ({longRangeCampaigns.length})
            </span>
            <ExpandIcon expanded={isLongRangeExpanded}>
              <ChevronDown />
            </ExpandIcon>
          </ChartHeader>
          <LongRangeContent expanded={isLongRangeExpanded}>
            <LongRangeContentInner expanded={isLongRangeExpanded}>
              <LongRangeWrapper>
                {longRangeCampaigns.map((campaign: CampaignSearchHit, index: number) => (
                  <LongRangeCampaign key={index}>
                    <CampaignName onClick={() => navigate(`/campaigns/campaign/${campaign.id}`)}>
                      {campaign.name}
                    </CampaignName>
                    <CampaignDates>
                      {dayjs(campaign.startDate).format("MMM DD, YYYY")} -{" "}
                      {dayjs(campaign.endDate).format("MMM DD, YYYY")}
                    </CampaignDates>
                  </LongRangeCampaign>
                ))}
              </LongRangeWrapper>
            </LongRangeContentInner>
          </LongRangeContent>
        </LongRangeSection>
      )}
    </>
  )
}

export default CampaignTimeLine
